import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { AuthLayout } from "~/components/auth/auth-layout";
import { NewPasswordForm } from "~/components/auth/new-password-form";
import { Alert } from "~/components/ui/feedback";
import { AuthError, authApi } from "~/lib/auth";
import { invalidLinkMessage } from "./reset-password";

/** Invitation landing page: new users choose their first password here. */
export const Route = createFileRoute("/_auth/set-password")({
  validateSearch: z.object({ token: z.string().optional().catch(undefined) }),
  component: SetPasswordPage,
});

function SetPasswordPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();

  if (!token) {
    return (
      <AuthLayout title="Welcome to Purlink">
        <Alert tone="error" title="This invitation link is incomplete.">
          Open the link from your invitation email again, or ask your administrator to resend it.
        </Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Welcome to Purlink"
      description="Choose a password to activate your account. Next, you'll set up two-step sign-in."
    >
      <NewPasswordForm
        submitLabel="Set password"
        onSubmit={async (password) => {
          try {
            await authApi.resetPassword(token, password);
          } catch (error) {
            if (invalidLinkMessage(error)) {
              throw new AuthError(
                400,
                "This invitation has expired or was already used. Ask your administrator to resend it.",
              );
            }
            throw error;
          }
          await navigate({ to: "/login", search: { reason: "reset" } });
        }}
      />
    </AuthLayout>
  );
}
